/* eslint-disable react/jsx-props-no-spreading, react/prop-types */
import React from 'react';
import omit from 'lodash/omit';
import { Row, Col } from 'antd';

import { Button, Radio } from '@atoms';
import { TextField, PageOrUrlField } from '@molecules';
import DocumentField from '@organisms/DocumentField';

import Form from '../Form';

import { LINKS_FORM_FIELDS } from './constants';
import * as s from './styles.module.less';

const LinkForm = React.forwardRef(({ initialValues, onSubmit, onDelete, disabled, onCancel }, ref) => {
  const [form] = Form.useForm();

  React.useImperativeHandle(ref, () => form);

  const [source, setSource] = React.useState(initialValues?.fileId ? 'file' : 'url');

  const pageOrUrlRef = React.useRef();

  const handleOk = React.useCallback(
    ({ page, ...values }) => {
      const setId = page?.contentId || page?.id;
      form.resetFields();
      // see explanation in PageOrUrl field
      // eslint-disable-next-line no-unused-expressions
      pageOrUrlRef.current?.reset();
      onSubmit(source === 'file' ? omit(values, 'page', 'url') : omit({ pageId: setId, ...values }, 'fileId'));
    },
    [form, onSubmit, source, pageOrUrlRef],
  );

  const handleChangeSource = React.useCallback(
    ({ target: { value } }) => {
      setSource(value);
      form.setFieldsValue({ source: value });
    },
    [form],
  );

  return (
    <Form
      form={form}
      hideRequiredMark
      layout="vertical"
      wrapperCol={{ span: 24 }}
      initialValues={{
        ...initialValues,
        source,
      }}
      onFinish={handleOk}
    >
      <TextField
        {...LINKS_FORM_FIELDS.NAME}
        label={`${initialValues ? 'Edit' : 'Add'} a link:`}
        className={s.smallMargin}
        disabled={disabled}
      />

      <Radio.Group onChange={handleChangeSource} className={s.radioGroup} value={source}>
        <Row align="top" gutter={10}>
          <Col flex="0" className={s.radio}>
            <Radio value="url" disabled={disabled} />
          </Col>
          <Col flex="auto">
            <PageOrUrlField
              {...LINKS_FORM_FIELDS.PAGE}
              rules={source === 'url' && LINKS_FORM_FIELDS.PAGE.rules}
              className={s.smallMargin}
              disabled={disabled || source !== 'url'}
              ref={pageOrUrlRef}
            />
          </Col>
        </Row>
        <Row align="top" gutter={10}>
          <Col flex="0" className={s.radio}>
            <Radio value="file" disabled={disabled} />
          </Col>
          <Col flex="1">
            <DocumentField
              {...LINKS_FORM_FIELDS.FILE}
              rules={source === 'file' && LINKS_FORM_FIELDS.FILE.rules}
              disabled={disabled || source !== 'file'}
            />
          </Col>
        </Row>
      </Radio.Group>

      {initialValues ? (
        <Row align="middle" gutter={20}>
          <Col flex="0 0 auto">
            <Button className="ant-btn-customBlue" disabled={disabled} onClick={form.submit}>
              SAVE CHANGES
            </Button>
          </Col>
          <Col flex="0 0 auto">
            <Button onClick={onCancel} disabled={disabled}>
              CANCEL
            </Button>
          </Col>
          <Col flex="0 0 1" align="right">
            <Button onClick={onDelete} disabled={disabled}>
              DELETE
            </Button>
          </Col>
        </Row>
      ) : (
        <Button className="ant-btn-customBlue" onClick={form.submit} disabled={disabled}>
          ADD LINK
        </Button>
      )}
    </Form>
  );
});

export default LinkForm;
